import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { DebitNoteService } from '../../services/debitNote.service';
import { SupplierBillService } from '../../services/supplierBill.service';
import { SupplierBill } from '../../interfaces/procurement/supplierBill.interface';
import { DebitableLine } from '../../interfaces/procurement/debitNote.interface';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';

const round2 = (n: number) => Math.round(n * 100) / 100;

export const DebitNoteCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const lockedBillId = searchParams.get('supplierBillId');

  const [bills, setBills] = useState<SupplierBill[]>([]);
  const [billId, setBillId] = useState(lockedBillId ?? '');
  const [lines, setLines] = useState<DebitableLine[]>([]);
  const [amounts, setAmounts] = useState<Record<string, number | ''>>({});
  const [noteDate, setNoteDate] = useState(toLocalIsoDate());
  const [reason, setReason] = useState('');
  const [supplierReference, setSupplierReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lockedBillId) return;
    SupplierBillService.listByInstitution(0, 500)
      .then(r => setBills((r.content ?? []).filter(b => ['UNPAID', 'PARTIALLY_PAID', 'PAID'].includes(b.status))))
      .catch(e => toast.showApiError(e, 'Failed to load supplier bills'));
  }, [lockedBillId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAmounts({});
    if (!billId) { setLines([]); return; }
    DebitNoteService.debitableLines(billId)
      .then(setLines)
      .catch(e => toast.showApiError(e, 'Failed to load bill lines'));
  }, [billId]); // eslint-disable-line react-hooks/exhaustive-deps

  const picked = useMemo(
    () => lines.filter(l => amounts[l.supplierBillLineId] !== undefined && Number(amounts[l.supplierBillLineId]) > 0),
    [lines, amounts]);
  const overLine = picked.some(l => Number(amounts[l.supplierBillLineId]) > l.remainingNet + 0.005);
  const netTotal = round2(picked.reduce((s, l) => s + Number(amounts[l.supplierBillLineId]), 0));
  const invalid = !billId || picked.length === 0 || overLine || !reason.trim() || !noteDate;

  const handleSave = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      const created = await DebitNoteService.create({
        supplierBillId: billId,
        noteDate,
        reason: reason.trim(),
        supplierReference: supplierReference.trim() || undefined,
        lines: picked.map(l => ({ supplierBillLineId: l.supplierBillLineId, amount: Number(amounts[l.supplierBillLineId]) })),
      });
      toast.showSuccess(`Debit note ${created.debitNoteNumber} posted`);
      navigate(`/procurement/debit-notes/${created.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create debit note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-help-page="procurement.dnForm.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>New Debit Note</Typography>
      <Paper data-help-section="procurement.dnForm.section.header" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {!lockedBillId && (
              <Box sx={{ flex: '1 1 320px', maxWidth: 520 }}>
                <SearchableSelect<SupplierBill>
                  data-help="procurement.dnForm.field.bill"
                  label="Supplier bill"
                  required
                  options={bills}
                  value={billId}
                  onChange={setBillId}
                  getOptionLabel={b => `${b.billNumber} - ${b.supplierName ?? ''} - ${formatMoney(b.totalAmount)}`}
                  noOptionsText="No posted bills"
                />
              </Box>
            )}
            <Box sx={{ flex: '1 1 180px', maxWidth: 240 }}>
              <TextField data-help="procurement.dnForm.field.date" label="Note date" type="date" required fullWidth value={noteDate}
                onChange={e => setNoteDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
              <TextField data-help="procurement.dnForm.field.ref" label="Supplier's credit note ref" fullWidth value={supplierReference}
                onChange={e => setSupplierReference(e.target.value)} inputProps={{ maxLength: 255 }} />
            </Box>
          </Box>
          <TextField data-help="procurement.dnForm.field.reason" label="Reason" required multiline minRows={2} fullWidth value={reason}
            onChange={e => setReason(e.target.value)} inputProps={{ maxLength: 1000 }} />
          <Alert severity="info">
            A debit note is a price or value adjustment against the bill; it does not return stock (record a physical return to the supplier as a separate stock-out). The credit goes to the price variance (PPV) account, never to GRNI. Enter the net
            amount per line (up to what is still debitable). Input tax is reduced in proportion, on the note date.
            The payable falls by the note total; any excess stays as an unapplied debit you can apply to another bill.
          </Alert>
          <ScrollableTable>
            <Table size="small" data-help-section="procurement.dnForm.lines">
              <TableHead>
                <TableRow>
                  <TableCell data-help="procurement.dnForm.col.line">Line</TableCell>
                  <TableCell data-help="procurement.dnForm.col.total" align="right">Line total</TableCell>
                  <TableCell data-help="procurement.dnForm.col.done" align="right">Already debited</TableCell>
                  <TableCell data-help="procurement.dnForm.col.left" align="right">Still debitable</TableCell>
                  <TableCell data-help="procurement.dnForm.col.debit" align="right">Debit (net)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map(l => {
                  const v = amounts[l.supplierBillLineId] ?? '';
                  const over = v !== '' && Number(v) > l.remainingNet + 0.005;
                  return (
                    <TableRow key={l.supplierBillLineId}>
                      <TableCell>
                        {l.description ?? '—'}
                        {!l.debitable && (
                          <Typography variant="caption" color="error" display="block">{l.notDebitableReason}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{formatMoney(l.lineTotal)}</TableCell>
                      <TableCell align="right">{formatMoney(l.debitedNet)}</TableCell>
                      <TableCell align="right">{formatMoney(l.remainingNet)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                          <TextField size="small" type="number" disabled={!l.debitable || l.remainingNet <= 0}
                            data-help="procurement.dnForm.field.amount"
                            value={v} error={over}
                            inputProps={{ min: 0, step: '0.01', 'aria-label': `Debit amount for ${l.description ?? 'line'}` }}
                            onChange={e => setAmounts(a => ({ ...a, [l.supplierBillLineId]: e.target.value === '' ? '' : Number(e.target.value) }))}
                            sx={{ flex: '1 1 110px', minWidth: 100, maxWidth: 200 }} />
                          <Button data-help="procurement.dnForm.full" size="small" disabled={!l.debitable || l.remainingNet <= 0}
                            onClick={() => setAmounts(a => ({ ...a, [l.supplierBillLineId]: l.remainingNet }))}>
                            Full
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollableTable>
          <Typography>Net debit: <b>{formatMoney(netTotal)}</b> (tax added on posting)</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button data-help="procurement.dnForm.save" variant="contained" onClick={handleSave} disabled={saving || invalid}>
              {saving ? 'Posting…' : 'Post debit note'}
            </Button>
            <Button data-help="procurement.dnForm.cancel" variant="outlined" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default DebitNoteCreate;
